import getConfig from 'next/config';
import Cookies from 'cookies';
import Head from 'next/head';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { t } from 'localization/utils/t';
import jwt from 'jsonwebtoken';

import { BFLanding } from 'blackFriday/components/BFLanding';
import { BFLandingFooter } from 'blackFriday/components/BFLandingFooter';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { DESKTOP_BANNER_HEIGHT, MOBILE_BANNER_HEIGHT } from 'banner/enums/constants';
import { DESKTOP_HEADER_HEIGHT, NEW_NAV_MOBILE_HEADER_HEIGHT } from 'layout/header/enums/constants';
import { FeatureFlags } from 'featureFlags/constants';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { IUser } from 'auth/types/User';
import { pruneUserFields } from 'server/controllers/auth/logInController';
import { setIsFooterVisible } from 'store/slice/pageLayoutSlice';
import { sneakersRequest } from 'server/services/sneakers/sneakersRequest';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useIsBannerVisible } from 'shared/hooks/useIsBannerVisible';
import { useIsMounted } from 'shared/hooks/useIsMounted';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const TP = 'pages.blackfriday.index';

const BlackFridayLandingPage = () => {
  const { isInApp } = useStoreSelector((state) => state.pageLayout);
  const { isDesktop } = useWindowSize();

  const storeDispatch = useStoreDispatch();

  const [isPreBlackFriday, isPostBlackFriday] = useFeatureFlag([
    FeatureFlags.WEB_ENABLE_PRE_BLACK_FRIDAY,
    FeatureFlags.WEB_ENABLE_POST_BLACK_FRIDAY,
  ]);

  const isBannerVisible = useIsBannerVisible();
  const isMounted = useIsMounted();

  const videoSrc = 'https://cms-cdn.goat.com/af5a95f78d7a-cc19-fe11-5e5a-040c2cfb.mp4';

  const [hasRendered, setHasRendered] = useState(false);

  useEffect(() => {
    storeDispatch(setIsFooterVisible(false));

    /**
     * The BF landing page is SSR and the video for the post black friday view does not
     * re-render after the initial load of the app. These 3 lines ensure the
     * video uses mobile for mobile/desktop for desktop, when the UI is refreshed
     * instead of using the default for all which is desktop for all views
     */
    if (isMounted()) {
      setHasRendered(true);
    }
  }, []);

  return (
    <BlackFridayLandingPage.Container
      $isBannerVisible={isBannerVisible}
      $isPostBF={isPostBlackFriday}
    >
      <Head>
        <title>{t(`${TP}.blackFridayTitle`, 'GOAT | Black Friday 2025')}</title>
        <meta
          property="og:title"
          content={t(`${TP}.blackFridayTitle`, 'GOAT | Black Friday 2025')}
          key="og:title"
        />
        <meta
          name="description"
          content={t(
            `${TP}.blackFridayMetadataDescription`,
            'The stars have aligned — GOAT Black Friday 2025 is here. Discover free prizes, exclusive sneakers, daily drops and more. Download the GOAT app now to start your cosmic journey.',
          )}
          key="description"
        />
        <meta
          property="og:title"
          content={t(`${TP}.blackFridayOgTitle`, 'GOAT Black Friday 2025')}
          key="og:title"
        />
        <meta
          property="og:description"
          content={t(
            `${TP}.blackFridayMetadataDescription`,
            'The stars have aligned — GOAT Black Friday 2025 is here. Discover free prizes, exclusive sneakers, daily drops and more. Download the GOAT app now to start your cosmic journey.',
          )}
          key="og:description"
        />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Event',
              name: t(`${TP}.blackFridayOgTitle`, 'GOAT Black Friday 2025'),
              startDate: '2025-11-21T09:30:00-08:00',
              endDate: '2025-11-27T23:59:00-08:00',
              eventStatus: 'https://schema.org/EventScheduled',
              eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
              location: {
                '@type': 'VirtualLocation',
                url: 'https://www.goat.com/blackfriday',
              },
              image: 'https://image.goat.com//images/icons/goat-logo-512.png',
              description: t(
                `${TP}.blackFridayMetadataDescription`,
                'The stars have aligned — GOAT Black Friday 2025 is here. Discover free prizes, exclusive sneakers, daily drops and more. Download the GOAT app now to start your cosmic journey.',
              ),
              organizer: {
                '@type': 'Organization',
                name: t(`${TP}.ldJsonOrganizer`, 'GOAT'),
                url: 'https://www.goat.com/',
              },
            }),
          }}
        />
      </Head>
      {hasRendered && (
        <>
          {isPostBlackFriday || isPreBlackFriday ? (
            <>
              <BlackFridayLandingPage.PrePostContentContainer
                $isPost={isPostBlackFriday}
                $isBannerVisible={isBannerVisible}
              >
                {isPostBlackFriday ? (
                  <>
                    {isDesktop && (
                      <BlackFridayLandingPage.PostBFVideo muted autoPlay playsInline loop>
                        <source
                          src="https://cms-cdn.goat.com/bb897f22d857-2e48-fe11-68ea-05fbaede.mp4"
                          type="video/mp4"
                        />
                      </BlackFridayLandingPage.PostBFVideo>
                    )}
                    {isDesktop && (
                      <BlackFridayLandingPage.PostImageOverlay src="https://cms-cdn.goat.com/3000/66f209190ab9-6aa8-fe11-88ea-0198b989.png" />
                    )}
                    {!isDesktop && (
                      <BlackFridayLandingPage.PostBFImage src="https://cms-cdn.goat.com/3000/c1636012387e-5e89-fe11-88ea-0e6d42c9.jpg" />
                    )}
                  </>
                ) : (
                  <>
                    <BlackFridayLandingPage.PreBFVideo muted autoPlay playsInline loop>
                      <source src={videoSrc} type="video/mp4" />
                    </BlackFridayLandingPage.PreBFVideo>
                    <p>
                      Over $10,000,000 in prizes.
                      <BlackFridayLandingPage.SpecialLineBreak /> 8 days of back-to-back drops.
                      <br />
                      Unreleased sneakers to sold-out It bags.
                      <BlackFridayLandingPage.SpecialLineBreak /> Rare design objects to archival
                      runway fashion.
                      <br />
                      Black Friday starts November 22. Only on the app.
                    </p>
                  </>
                )}
              </BlackFridayLandingPage.PrePostContentContainer>
              {!isInApp && <BFLandingFooter isPostBlackFriday={isPostBlackFriday} />}
            </>
          ) : (
            <BFLanding />
          )}
        </>
      )}
    </BlackFridayLandingPage.Container>
  );
};

export const getServerSideProps = async ({ req, res, locale }) => {
  const {
    serverRuntimeConfig: { BLACKOUT_NAMESPACE, JWT_COOKIE_NAME, COOKIE_ENCRYPTION_KEY },
    publicRuntimeConfig: { GOATENV_ENVIRONMENT, RELEASE_ENV },
  } = getConfig();
  const { featureFlags, headers, query } = req;

  if (query?.test === 'true' && featureFlags?.[FeatureFlags.WEB_ENABLE_AUCTIONS]) {
    try {
      const cookies = new Cookies(req, res, { secure: RELEASE_ENV !== 'development' });
      let jwtData = cookies.get(JWT_COOKIE_NAME);
      if (jwtData) {
        return {
          redirect: {
            destination: getRedirectionPath('/auctions', locale),
            permanent: false,
          },
        };
      }
      const { authorization } = headers;
      const authToken = authorization?.match(/"(.*)"/)[1];
      res.locals.user = { authToken };
      const response = await sneakersRequest(req, res).get('api/v1/users/me');
      const userData = response.body as unknown as IUser;
      const jwtUserPayload = pruneUserFields({ ...userData, authToken });
      jwtData = jwt.sign(jwtUserPayload, COOKIE_ENCRYPTION_KEY);
      cookies.set(JWT_COOKIE_NAME, jwtData, {
        httpOnly: true,
        sameSite: 'strict',
      });
      return {
        redirect: {
          destination: getRedirectionPath('/auctions', locale),
          permanent: false,
        },
      };
    } catch (error) {
      bugsnag(error, { context: 'Issue Authorizing Mobile Apps in Webview' });
    }
  }

  // If we're trying to access trivia through web view & trivia FF is enabled
  if (query?.triviaId && featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_BLACK_FRIDAY_TRIVIA]) {
    try {
      let triviaPath = `/blackfriday/trivia/${query?.triviaId as string}/lobby?app=y`;

      const userAgent = headers?.['user-agent'];
      const localeFromApp = String(
        userAgent?.match(/Locale\/([#A-Z_a-z]+)/)?.[1] || '',
      )?.toLowerCase();
      const cookies = new Cookies(req, res, { secure: RELEASE_ENV !== 'development' });
      const country = cookies.get('country')?.toLowerCase();

      if (localeFromApp) {
        if (localeFromApp === 'ja') {
          triviaPath = `/ja-jp/blackfriday/trivia/${query?.triviaId as string}/lobby?app=y`;
        } else if (localeFromApp === 'fr') {
          triviaPath = getRedirectionPath(
            `/blackfriday/trivia/${query?.triviaId as string}/lobby?app=y`,
            'fr-fr',
          );
        }
      } else if (country === 'jp') {
        triviaPath = `/ja-jp/blackfriday/trivia/${query?.triviaId as string}/lobby?app=y`;
      } else if (country === 'fr') {
        triviaPath = getRedirectionPath(
          `/blackfriday/trivia/${query?.triviaId as string}/lobby?app=y`,
          'fr-fr',
        );
      }

      const jwtData = cookies.get(JWT_COOKIE_NAME);
      if (jwtData) {
        return {
          redirect: {
            destination: triviaPath,
            permanent: false,
          },
        };
      }

      const { authorization } = headers;
      const authToken = authorization?.match(/"(.*)"/)[1];
      res.locals.user = { authToken };

      const userResponse = await sneakersRequest(req, res).get('api/v1/users/me');
      const userData = userResponse.body as unknown as IUser;

      const userAuthPayload = pruneUserFields({ ...userData, authToken });
      cookies.set(JWT_COOKIE_NAME, JSON.stringify(userAuthPayload), {
        httpOnly: true,
        sameSite: 'strict',
      });

      return {
        redirect: {
          destination: triviaPath,
          permanent: false,
        },
      };
    } catch (error) {
      bugsnag(error, { context: 'Black Friday Landing Page' });
      return {
        props: {
          auth: res?.locals,
          isBlackoutAuthFailed: true,
          namespace: headers?.['x-goat-namespace'] || BLACKOUT_NAMESPACE,
        },
      };
    }
  }

  // if trying to access blackout through web view & blackout FF is enabled
  if (
    (query?.blackout === 'true/' &&
      featureFlags?.[FeatureFlags.WEB_ENABLE_BLACK_FRIDAY_BLACKOUT]) ||
    (query?.auctionId && featureFlags?.[FeatureFlags.WEB_ENABLE_AUCTIONS])
  ) {
    try {
      const cookies = new Cookies(req, res, { secure: RELEASE_ENV !== 'development' });
      const jwtData = cookies.get(JWT_COOKIE_NAME);
      if (jwtData) {
        return {
          redirect: {
            destination: getRedirectionPath(
              `/auctions/${query?.auctionId as string}?app=y`,
              locale,
            ),
            permanent: false,
          },
        };
      }
      const { authorization } = headers;
      const authToken = authorization?.match(/"(.*)"/)[1];
      res.locals.user = { authToken };
      const response = await sneakersRequest(req, res).get('api/v1/users/me');
      const userData = response.body as unknown as IUser;
      const userAuthPayload = pruneUserFields({ ...userData, authToken });
      cookies.set(JWT_COOKIE_NAME, JSON.stringify(userAuthPayload), {
        httpOnly: true,
        sameSite: 'strict',
      });
      return {
        redirect: {
          destination: getRedirectionPath(`/auctions/${query?.auctionId as string}?app=y`, locale),
          permanent: false,
        },
      };
    } catch (error) {
      bugsnag(error, { context: 'BlackFridayLandingPage' });
      return {
        props: {
          auth: res?.locals,
          isBlackoutAuthFailed: true,
          namespace: headers?.['x-goat-namespace'] || BLACKOUT_NAMESPACE,
        },
      };
    }
  }

  return {
    props: {
      auth: res?.locals,
      isBlackoutAuthFailed: false,
      namespace: GOATENV_ENVIRONMENT || headers?.['x-goat-namespace'] || BLACKOUT_NAMESPACE,
    },
  };
};

const DESKTOP_HEADER_AND_BANNER_HEIGHT = `calc(${DESKTOP_HEADER_HEIGHT} + ${DESKTOP_BANNER_HEIGHT})`;
const MOBILE_HEADER_AND_BANNER_HEIGHT = `calc(${NEW_NAV_MOBILE_HEADER_HEIGHT} + ${MOBILE_BANNER_HEIGHT})`;

BlackFridayLandingPage.Container = styled.div<{ $isBannerVisible: boolean; $isPostBF: boolean }>`
  height: calc(
    100vh -
      ${({ $isBannerVisible }) =>
        $isBannerVisible ? MOBILE_HEADER_AND_BANNER_HEIGHT : NEW_NAV_MOBILE_HEADER_HEIGHT}
  );

  ${hideScrollbars};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    height: calc(
      100vh -
        ${({ $isBannerVisible }) =>
          $isBannerVisible ? DESKTOP_HEADER_AND_BANNER_HEIGHT : DESKTOP_HEADER_HEIGHT}
    );
  }
`;

BlackFridayLandingPage.PreBFVideo = styled.video`
  max-height: 64px;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    max-height: 96px;
  }
`;

BlackFridayLandingPage.PostBFVideo = styled.video`
  position: absolute;
  top: 50px;
  width: 100%;
  max-height: 90%;
  height: 90%;
`;

BlackFridayLandingPage.PostBFImage = styled.img`
  position: absolute;
  top: 50px;
  max-width: 100%;
  max-height: 90%;
  height: 90%;
`;

BlackFridayLandingPage.PostImageOverlay = styled.img`
  bottom: 0;
  display: none;
  left: 0;
  margin: auto;
  position: absolute;
  right: 0;
  top: 0;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: block;
  }
`;

BlackFridayLandingPage.PrePostContentContainer = styled.div<{
  $isBannerVisible: boolean;
  $isPost?: boolean;
}>`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0px;
  position: fixed;
  top: ${({ $isBannerVisible }) =>
    $isBannerVisible ? MOBILE_HEADER_AND_BANNER_HEIGHT : NEW_NAV_MOBILE_HEADER_HEIGHT};
  bottom: 56px;
  margin: auto;
  left: 0px;
  right: 0px;
  text-align: center;

  p {
    font-family: GOATProduct-MonoRegular;
    font-size: 10.5px;
    line-height: 13.5px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    bottom: ${({ $isPost }) => ($isPost ? '0px' : '70px')};
    top: ${({ $isBannerVisible }) =>
      $isBannerVisible ? DESKTOP_HEADER_AND_BANNER_HEIGHT : DESKTOP_HEADER_HEIGHT};
  }
`;

BlackFridayLandingPage.SpecialLineBreak = styled.br`
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

export default BlackFridayLandingPage;
